/**
 * Baltic formatting utilities that match the Angular implementation
 */

import type { BalticRoute } from '@/types/baltic.types';

/**
 * BIDate is non-nullable in the API DTO, so a missing date arrives as DateTime.MinValue
 * ("0001-01-01T00:00:00") rather than null. Treat both as "no date".
 */
export function normalizeBalticDate(date?: string | null): string | null {
  if (!date) return null;
  return date.startsWith('0001-01-01') ? null : date;
}

/**
 * A route has a TI assessment only when the API sends a TI date. TI values themselves are
 * serialized as non-nullable decimals, so a missing assessment is indistinguishable from 0.
 */
export function hasTIAssessment(route: Pick<BalticRoute, 'tiDate'>): boolean {
  return normalizeBalticDate(route.tiDate) !== null;
}

/**
 * Date shown for a route: the TI date, falling back to the BI date
 */
export function getBalticRouteDate(route: Pick<BalticRoute, 'tiDate' | 'biDate'>): string | null {
  return normalizeBalticDate(route.tiDate) ?? normalizeBalticDate(route.biDate);
}

/**
 * Get currency symbol based on decimals (matches Angular currencySymbol getter)
 */
export function getCurrencySymbol(decimals: number): string {
  return decimals === 0 ? '$' : 'WS';
}

/**
 * Format Baltic value with proper currency and decimal places (matches Angular)
 */
export function formatBalticValue(
  value: number | undefined,
  decimals: number,
  showCurrency: boolean = true
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return showCurrency
      ? `${getCurrencySymbol(decimals)}0.${'0'.repeat(decimals)}`
      : `0.${'0'.repeat(decimals)}`;
  }

  const currencySymbol = getCurrencySymbol(decimals);
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return showCurrency ? `${currencySymbol}${formatted}` : formatted;
}

/**
 * Format Baltic change value with proper sign and decimal places (matches Angular)
 */
export function formatBalticChange(change: number | undefined, decimals: number): string {
  if (change === undefined || change === null || isNaN(change)) {
    return `0.${'0'.repeat(decimals)}`;
  }

  const formatted = Math.abs(change).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return change >= 0 ? `+${formatted}` : `-${formatted}`;
}

/**
 * Format TCE value with $ and no decimals (matches Angular '1.0-0' format)
 */
export function formatTCEValue(value: number | undefined): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '$0';
  }

  // Angular shows TCE values as-is with no division by 1000, just rounded to no decimals
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

/**
 * Get change color class based on value
 */
export function getChangeColorClass(change: number | undefined): string {
  if (!change || isNaN(change)) return 'text-gray-500 dark:text-gray-400';
  if (change > 0) return 'text-green-600 dark:text-green-400';
  if (change < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-500 dark:text-gray-400';
}

/**
 * Format chart tooltip value based on TCE mode and currency
 */
export function formatChartTooltipValue(
  value: number,
  showTCE: boolean,
  decimals: number = 2
): string {
  if (showTCE) {
    // For TCE mode, show as $ with no decimals
    return `$${Math.round(value).toLocaleString('en-US')}`;
  } else {
    // For regular mode, use WS with appropriate decimals
    const currency = getCurrencySymbol(decimals);
    const format = currency === 'WS' ? decimals : 0; // Use more precision for WS in tooltips
    return `${currency}${value.toLocaleString('en-US', {
      minimumFractionDigits: format,
      maximumFractionDigits: format,
    })}`;
  }
}

/**
 * Format chart axis tick value
 */
export function formatChartTickValue(
  value: number,
  showTCE: boolean,
  decimals: number = 2
): string {
  if (showTCE) {
    return `$${Math.round(value).toLocaleString('en-US')}`;
  } else {
    const currency = getCurrencySymbol(decimals);
    return `${currency}${Math.round(value).toLocaleString('en-US')}`;
  }
}

/**
 * Get chart dataset configuration based on TCE mode (matches Angular logic)
 */
export function getChartDatasets(showTCE: boolean) {
  if (showTCE) {
    return [
      {
        key: 'bitrtce' as const,
        label: 'BITR TCE',
        color: '#3B82F6',
      },
      {
        key: 'tiScrubber' as const,
        label: 'TI SCRUBBER',
        color: '#10B981',
      },
      {
        key: 'tivlsfo' as const,
        label: 'TI NO SCRUB',
        color: '#8B5CF6',
      },
    ];
  } else {
    return [
      {
        key: 'biValue' as const,
        label: 'BITR',
        color: '#3B82F6',
      },
      {
        key: 'tiValue' as const,
        label: 'TI',
        color: '#F59E0B',
      },
    ];
  }
}
